import React, { useCallback, useContext, useEffect, useState } from 'react'
import {
  FlatList,
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  View,
} from 'react-native'
import {
  BG_STYLES,
  BORDER_STYLES,
  BottomDrawer,
  Button,
  BUTTON_STATUS,
  BUTTON_TYPE,
  CenterModal,
  Chip,
  CHIP_STATES,
  COLORS,
  COMMON_STYLES,
  CrossIcon,
  DeleteIcon,
  FilterIcon,
  RadioButton,
  SIZE,
  Spinner,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from '@dehaat/dds'
import RequestCard from './RequestCard'
import RequestInfo from './RequestInfo'
import NewRequestCTA from './NewRequestCTA'

import ApproveIcon from 'app/icons/Approve'
import useChangePrice from 'app/hooks/useChangePrice'
import { Approver, PriceChangeRequest } from 'app/models/RequestFlow'
import withdrawRequest from 'app/apis/withdrawRequest'
import { useDispatch, useSelector } from 'react-redux'
import {
  ROLE_TYPES,
  selectRoleType,
  setUserRole,
} from 'app/store/reducer/requestflow'
import fetchUserRole from 'app/apis/fetchUserRole'
import { ResponsiveContext } from 'app/context/ResponsiveUIContext'
import ToastAndroid from 'app/mocks/ToastAndroid'
import { RequestAction, RoleType, STATUS } from 'app/constants/request'
import { isMobileDevice, postMessageToWebView } from 'app/utils/common'
import { useFocusEffect } from '@react-navigation/native'
import FilterModal from './FilterModal'
import RemovableFilterChip from 'app/components/reusable/RemovableFilterChip'
import validateRequest from 'app/apis/validateRequest'
import BouncyCheckbox from 'react-native-bouncy-checkbox'
import RoleSelection from './RoleSelection'
import OutlinedInput from './OutlinedInput'
import fetchCancelReasons from 'app/apis/fetchCancelReasons'

const STATUS_FILTERS = ['Pending', 'Approved', 'Rejected']
const KEYS_HIDDEN_FROM_CHIPS = new Set(['status', 'created_at_before', 'created_at_after'])

const formatFilterValue = (val: unknown): string => {
  if (val == null) return ''
  if (Array.isArray(val)) {
    return val
      .map(item => {
        if (item == null) return ''
        if (typeof item === 'string' || typeof item === 'number') return String(item)
        if (typeof item === 'object') {
          const obj = item as Record<string, unknown>
          return String(obj.name ?? obj.label ?? obj.value ?? obj.id ?? '')
        }
        return String(item)
      })
      .filter(Boolean)
      .join(', ')
  }
  if (typeof val === 'object') {
    const obj = val as Record<string, unknown>
    return String(obj.name ?? obj.label ?? obj.value ?? obj.id ?? '')
  }
  return String(val)
}

type AppliedFilters = {
  created_at_after?: string
  created_at_before?: string
  status?: string
  request_type?: string
}
type RejectReason = {
  id: number
  reason: string
}

interface HomePriceRequestProps {
  resetFilters?: boolean
}

const HomePriceRequest = ({ resetFilters }: HomePriceRequestProps) => {
  const [showDrawer, setShowDrawer] = useState(false)
  const [selectedItem, setSelectedItem] = useState<PriceChangeRequest | null>(
    null,
  )
  const [showFilterModal, setShowFilterModal] = useState(false)
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({})
  const [showConfirm, setShowConfirm] = useState(false)
  const [confirmAction, setConfirmAction] = useState<RequestAction | null>(null)
  const [isEnabled, setIsEnabled] = useState(false)
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const dispatch = useDispatch()
  const roleType = useSelector(selectRoleType)
  const [roleLoading, setRoleLoading] = useState(true)
  const { mediaStyles } = useContext(ResponsiveContext)
  const isDesktop = !isMobileDevice()
  const [statusFilter, setStatusFilter] = useState<string>(
    appliedFilters.status || STATUS_FILTERS[0],
  )
  const [showRoleSelection, setShowRoleSelection] = useState(false)
  const [availableRoles, setAvailableRoles] = useState<any[]>([])
  const [showNoRole, setNoRole] = useState(false)
  const isRoleType = (value: string): value is RoleType => {
    return ROLE_TYPES.includes(value as RoleType)
  }
  const [roleInitialized, setRoleInitialized] = useState(false)
  const [showRejectDrawer, setShowRejectDrawer] = useState(false)
  const [selectedReasonIndex, setSelectedReasonIndex] = useState(-1)
  const [otherReason, setOtherReason] = useState('')
  const [rejectLoading, setRejectLoading] = useState(false)
  const [reasonList, setReasonList] = useState<RejectReason[]>([])
  const { fetchNext, request, loading, loadingNext, getRequest } =
    useChangePrice({
      request_type: 'PRICE_CHANGE',
      status: appliedFilters.status || statusFilter,
      roleType: roleType,
      created_at_after: appliedFilters.created_at_after,
      created_at_before: appliedFilters.created_at_before,
      enabled: roleInitialized,
    })
  useFocusEffect(
    useCallback(() => {
      if (roleInitialized) {
        getRequest()
      }
    }, [getRequest]),
  )

  const handleAction = async (
    action: RequestAction,
    ids?: number[],
    reasonPayload?: { reason_config_id: number | null; reason: string },
  ) => {
    const targetIds = ids ?? selectedItems
    if (!targetIds.length) return
    try {
      await validateRequest(action, targetIds, roleType, reasonPayload)
      setSelectedItems([])
      setIsEnabled(false)
      getRequest()
    } catch (err) {
      console.error(err)
      ToastAndroid.show('Something went wrong', ToastAndroid.SHORT)
    }
  }
  const handleRoleSelect = (selectedRole: any) => {
    dispatch(setUserRole(selectedRole))
    localStorage.setItem('UserRole', selectedRole)
    setShowRoleSelection(false)
    setRoleInitialized(true)
  }

  const handleApplyFilters = (filters: any) => {
    setAppliedFilters(filters)

    const matched = STATUS_FILTERS.find(
      s => s.toLowerCase() === String(filters.status).toLowerCase(),
    )
    if (matched) {
      setStatusFilter(matched)
    } else {
      setStatusFilter('')
    }

    setShowFilterModal(false)
  }

  const handleRemoveFilter = (key: string) => {
    setAppliedFilters(prev => {
      const next: AppliedFilters = { ...prev }
      if (key === 'date_range') {
        delete next.created_at_after
        delete next.created_at_before
      } else {
        delete next[key as keyof AppliedFilters]
      }
      return next
    })
    setSelectedItems([])
    setIsEnabled(false)
  }

  const toggleSelectItem = (id: number) => {
    setSelectedItems(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    )
  }
  const handleSelectAll = () => {
    if (selectedItems.length === request.length) {
      setSelectedItems([])
    } else {
      setSelectedItems(request.map(item => item.id))
    }
  }
  const fetchRejectReasons = async () => {
    try {
      setRejectLoading(true)

      const data = await fetchCancelReasons({
        action: 'UPDATE',
        entity: 'PRICE',
        requester: roleType,
        random: true,
        lang: 'en',
      })
      const formatted = data.map(item => ({
        id: item.id,
        reason: item.reason,
      }))

      formatted.push({
        id: -1,
        reason: '',
      })

      setReasonList(formatted)
    } catch {
      ToastAndroid.show('Unable to load reject reasons', ToastAndroid.SHORT)
    } finally {
      setRejectLoading(false)
    }
  }

  useEffect(() => {
    const initializeUserRole = async () => {
      try {
        const savedRole = localStorage.getItem('UserRole')
        if (savedRole && isRoleType(savedRole)) {
          dispatch(setUserRole(savedRole))
          setRoleLoading(false)
          setRoleInitialized(true)
          return
        }
        setRoleLoading(true)
        const apiResponse = await fetchUserRole()
        if (!apiResponse) {
          ToastAndroid.show('Something went wrong', ToastAndroid.SHORT)
          setRoleLoading(false)
          setRoleInitialized(true)
          return
        }
        if (apiResponse.role_type.length === 0) {
          setNoRole(true)
        } else if (apiResponse.role_type.length > 1) {
          setAvailableRoles(apiResponse.role_type)
          setShowRoleSelection(true)
        } else if (apiResponse.role_type.length === 1) {
          const singleRole = apiResponse.role_type[0]
          dispatch(setUserRole(singleRole))
          setRoleInitialized(true)
        } else {
          ToastAndroid.show('Invalid role data received', ToastAndroid.SHORT)
        }
      } catch (error) {
        ToastAndroid.show('Something went wrong', ToastAndroid.SHORT)
      } finally {
        setRoleLoading(false)
      }
    }

    initializeUserRole()
  }, [dispatch])

  const renderItem = ({ item }: { item: PriceChangeRequest }) => {
    const isSelected = selectedItems.includes(item.id)
    return (
      <RequestCard
        item={item}
        isMultiSelect={isEnabled}
        isSelected={isSelected}
        onToggleSelect={() => toggleSelectItem(item.id)}
        onApprove={() => {
          setSelectedItem(item)
          setConfirmAction(RequestAction.APPROVE)
          setShowConfirm(true)
        }}
        onReject={() => {
          setSelectedItem(item)
          setShowRejectDrawer(true)
        }}
        onPress={() => {
          if (isEnabled) {
            toggleSelectItem(item.id)
          } else {
            setSelectedItem(item)
            setShowDrawer(true)
          }
        }}
      />
    )
  }
  const handleSwitch = () => {
    const newValue = !isEnabled
    setIsEnabled(newValue)
  }
  const handleWithDraw = async (id: number) => {
    await withdrawRequest(id, roleType)
    setShowDrawer(false)
    getRequest()
  }

  const ApproverRow = ({
    level,
    approvers,
  }: {
    level: string
    approvers: Approver[]
  }) => {
    const approver = approvers.find(a => a.level === level)

    const getStatusText = (status?: string | null) => {
      switch (status) {
        case STATUS.PENDING:
          return 'Approval Pending'
        case STATUS.REJECTED:
          return 'Rejected'
        case STATUS.APPROVED:
          return 'Approved'
        default:
          return ''
      }
    }
    const getStatusColor = (status?: string | null) => {
      switch (status) {
        case STATUS.PENDING:
          return COLORS.yellow100
        case STATUS.REJECTED:
          return COLORS.error100
        case STATUS.APPROVED:
          return COLORS.success100
        default:
          return COLORS.yellow100
      }
    }
    return (
      <View style={styles.infoRow}>
        <Text
          style={[
            styles.labelText,
            mediaStyles({
              desktopStyle: {
                ...TYPOGRAPHY.CAPTION1,
              },
            }),
          ]}
        >
          {level} Approver:{' '}
        </Text>
        <Text
          style={[
            styles.valueText,
            mediaStyles({
              desktopStyle: {
                ...TYPOGRAPHY.CAPTION1,
              },
            }),
          ]}
        >
          {approver?.approver_name || '--'}
        </Text>
        <Text
          style={[
            styles.statusText,
            mediaStyles({
              desktopStyle: {
                ...TYPOGRAPHY.CAPTION1,
              },
            }),
            { color: getStatusColor(approver?.status) },
          ]}
        >
          {getStatusText(approver?.status)}
        </Text>
      </View>
    )
  }

  const canWithDrawRequest =
    selectedItem?.latest_status &&
    selectedItem.latest_status.toUpperCase().includes(STATUS.L1_PENDING)

  const formattedInventorySources =
    selectedItem?.inventory_source
      ?.split(',')
      .map((source: string) => `<${source.trim()}>`)
      .join(', ') || '--'

  const priceThresholdStatus =
    Number(selectedItem?.suggested_price) <
    Number(selectedItem?.threshold_price)
      ? 'lower'
      : 'higher'

  const confirmTitle =
    confirmAction === RequestAction.APPROVE
      ? 'Approve Request?'
      : 'Reject Request?'

  const confirmMessage =
    confirmAction === RequestAction.APPROVE
      ? `Are you sure you want to approve new selling price which is ${
          priceThresholdStatus
        } than the Threshold price?`
      : 'Are you sure you want to reject this request?'

  useEffect(() => {
    setAppliedFilters({
      status: STATUS_FILTERS[0].toUpperCase(),
    })
    setStatusFilter(STATUS_FILTERS[0])
  }, [resetFilters])
  useEffect(() => {
    if (showRejectDrawer) {
      fetchRejectReasons()
    }
  }, [showRejectDrawer])

  if (loading || roleLoading) return <Spinner />

  return (
    <View style={styles.wrapper}>
      <ScrollView
        style={[
          BG_STYLES.bgNeutral10,
          COMMON_STYLES.row,
          COMMON_STYLES.ph1,
          COMMON_STYLES['pv1/2'],
          { columnGap: 8, maxHeight: 52 },
        ]}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Pressable
          style={[
            BORDER_STYLES.borderNeutral20,
            COMMON_STYLES['br1/2'],
            COMMON_STYLES['mr0.5'],
            { borderWidth: 1, padding: 6 },
            BG_STYLES.bgWhite,
          ]}
          onPress={() => setShowFilterModal(true)}
        >
          <FilterIcon
            svgProps={{
              height: 22,
              width: 22,
              fill: COLORS.neutral70,
            }}
          />
        </Pressable>

        {STATUS_FILTERS.map(option => (
          <Chip
            key={option}
            label={option}
            state={
              statusFilter === option
                ? CHIP_STATES.ACTIVE
                : CHIP_STATES.INACTIVE
            }
            onClick={() => {
              console.log('first ==>>', statusFilter)
              setStatusFilter(option)
              setAppliedFilters(prev => ({
                ...prev,
                status: option.toUpperCase(),
              }))
            }}
            activeContainerStyle={[BG_STYLES.bgBlack]}
            iconStrokeColor="white"
            labelStyle={{
              color: statusFilter === option ? COLORS.white : COLORS.black,
            }}
          />
        ))}
        {Object.entries(appliedFilters).map(([key, val]) => {
          if (KEYS_HIDDEN_FROM_CHIPS.has(key)) return null
          const label = formatFilterValue(val)
          if (!label) return null
          return (
            <RemovableFilterChip
              key={`filter_${key}`}
              label={label}
              onRemove={() => handleRemoveFilter(key)}
            />
          )
        })}

        {appliedFilters.created_at_before &&
          appliedFilters.created_at_after && (
            <RemovableFilterChip
              key="date_range"
              label={`Date: ${appliedFilters.created_at_before} to ${appliedFilters.created_at_after}`}
              onRemove={() => handleRemoveFilter('date_range')}
            />
          )}
      </ScrollView>
      {(roleType === RoleType.L1 || roleType === RoleType.L2) &&
        request.length > 0 &&
        statusFilter == STATUS_FILTERS[0] && (
          <View
            style={[
              COMMON_STYLES.row,
              COMMON_STYLES.ph1,
              COMMON_STYLES['pt1/2'],
            ]}
          >
            <View style={{ flex: 1 }}>
              {isEnabled && (
                <Pressable onPress={handleSelectAll}>
                  <BouncyCheckbox
                    isChecked={selectedItems.length === request.length}
                    onPress={handleSelectAll}
                    size={20}
                    fillColor={COLORS.primary100}
                    iconStyle={{ borderRadius: 4 }}
                    innerIconStyle={{ borderRadius: 4 }}
                    text={
                      selectedItems.length === request.length
                        ? 'Unselect All'
                        : 'Select All'
                    }
                    textStyle={[
                      styles.selectAll,
                      TEXT_STYLES.textPrimary100,
                      { textDecorationLine: 'none' },
                    ]}
                  />
                </Pressable>
              )}
            </View>
            <View style={[COMMON_STYLES.row, { gap: 8 }]}>
              <Text style={[styles.selectAll, TEXT_STYLES.textNeutral90]}>
                Select multiple
              </Text>
              <Switch
                trackColor={{
                  false: COLORS.white,
                  true: COLORS.success10,
                }}
                thumbColor={isEnabled ? COLORS.primary120 : COLORS.neutral70}
                onValueChange={handleSwitch}
                value={isEnabled}
              />
            </View>
          </View>
        )}
      <View style={{ flex: 1 }}>
        <FlatList
          data={request}
          keyExtractor={item => item.id.toString()}
          renderItem={renderItem}
          onEndReached={fetchNext}
          showsVerticalScrollIndicator={false}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>No Requests Found</Text>
            </View>
          }
          contentContainerStyle={{ paddingBottom: 24 }}
          ListFooterComponent={
            loadingNext ? <Spinner type="in-screen" /> : null
          }
        />
      </View>
      {roleType === RoleType.REQUESTER && <NewRequestCTA />}
      {showDrawer && (
        <BottomDrawer
          visible={showDrawer}
          onClose={() => setShowDrawer(false)}
          closeOnBackdropPress
        >
          {selectedItem && <RequestInfo item={selectedItem} />}
          <View style={[COMMON_STYLES.pt1, COMMON_STYLES['ph1/2']]}>
            <View style={styles.infoRow}>
              <Text
                style={[
                  styles.labelText,
                  mediaStyles({
                    desktopStyle: {
                      ...TYPOGRAPHY.CAPTION1,
                    },
                  }),
                ]}
              >
                Request Id:{' '}
              </Text>
              <Text
                style={[
                  styles.valueText,
                  mediaStyles({
                    desktopStyle: {
                      ...TYPOGRAPHY.CAPTION1,
                    },
                  }),
                ]}
              >
                {selectedItem?.requested_by.id}
              </Text>
            </View>
            <View style={styles.infoRow}>
              <Text
                style={[
                  styles.labelText,
                  mediaStyles({
                    desktopStyle: {
                      ...TYPOGRAPHY.CAPTION1,
                    },
                  }),
                ]}
              >
                Requested by:{' '}
              </Text>
              <Text
                style={[
                  styles.valueText,
                  mediaStyles({
                    desktopStyle: {
                      ...TYPOGRAPHY.CAPTION1,
                    },
                  }),
                ]}
              >
                {selectedItem?.requested_by.full_name}
              </Text>
            </View>
            <View style={styles.infoRow}>
              <Text
                style={[
                  styles.labelText,
                  mediaStyles({
                    desktopStyle: {
                      ...TYPOGRAPHY.CAPTION1,
                    },
                  }),
                ]}
              >
                Request Reason:{' '}
              </Text>
              <Text
                style={[
                  styles.valueText,
                  mediaStyles({
                    desktopStyle: {
                      ...TYPOGRAPHY.CAPTION1,
                    },
                  }),
                ]}
              >
                {selectedItem?.request_reason || '--'}
              </Text>
            </View>
            <View style={styles.infoRow}>
              <Text
                style={[
                  styles.labelText,
                  mediaStyles({
                    desktopStyle: {
                      ...TYPOGRAPHY.CAPTION1,
                    },
                  }),
                ]}
              >
                Vendor Id:{' '}
              </Text>
              <Text
                style={[
                  styles.valueText,
                  mediaStyles({
                    desktopStyle: {
                      ...TYPOGRAPHY.CAPTION1,
                    },
                  }),
                ]}
              >
                {selectedItem?.vendor.id}
              </Text>
            </View>
            <View style={styles.infoRow}>
              <Text
                style={[
                  styles.labelText,
                  mediaStyles({
                    desktopStyle: {
                      ...TYPOGRAPHY.CAPTION1,
                    },
                  }),
                ]}
              >
                Vendor Name:{' '}
              </Text>
              <Text
                style={[
                  styles.valueText,
                  mediaStyles({
                    desktopStyle: {
                      ...TYPOGRAPHY.CAPTION1,
                    },
                  }),
                ]}
              >
                {selectedItem?.vendor.company_name}
              </Text>
            </View>
            <View style={styles.infoRow}>
              <Text
                style={[
                  styles.labelText,
                  mediaStyles({
                    desktopStyle: {
                      ...TYPOGRAPHY.CAPTION1,
                    },
                  }),
                ]}
              >
                Vendor Location:{' '}
              </Text>
              <Text
                style={[
                  styles.valueText,
                  mediaStyles({
                    desktopStyle: {
                      ...TYPOGRAPHY.CAPTION1,
                    },
                  }),
                ]}
              >
                {[
                  selectedItem?.vendor.district?.name,
                  selectedItem?.vendor.state?.name,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </Text>
            </View>
            <View style={styles.infoRow}>
              <Text
                style={[
                  styles.labelText,
                  mediaStyles({
                    desktopStyle: {
                      ...TYPOGRAPHY.CAPTION1,
                    },
                  }),
                ]}
              >
                Partner Id:{' '}
              </Text>
              <Text
                style={[
                  styles.valueText,
                  mediaStyles({
                    desktopStyle: {
                      ...TYPOGRAPHY.CAPTION1,
                    },
                  }),
                ]}
              >
                {selectedItem?.vendor.partner_id}
              </Text>
            </View>
            <View style={styles.infoRow}>
              <Text
                style={[
                  styles.labelText,
                  mediaStyles({
                    desktopStyle: {
                      ...TYPOGRAPHY.CAPTION1,
                    },
                  }),
                ]}
              >
                Inventory Source:{' '}
              </Text>
              <Text
                style={[
                  styles.valueText,
                  mediaStyles({
                    desktopStyle: {
                      ...TYPOGRAPHY.CAPTION1,
                    },
                  }),
                ]}
              >
                {formattedInventorySources}
              </Text>
            </View>
            <View style={styles.infoRow}>
              <Text
                style={[
                  styles.labelText,
                  mediaStyles({
                    desktopStyle: {
                      ...TYPOGRAPHY.CAPTION1,
                    },
                  }),
                ]}
              >
                B2B Price:{' '}
              </Text>
              <Text
                style={[
                  styles.valueText,
                  mediaStyles({
                    desktopStyle: {
                      ...TYPOGRAPHY.CAPTION1,
                    },
                  }),
                ]}
              >
                {selectedItem?.b2b_price}
              </Text>
            </View>
            <ApproverRow
              level={RoleType.L1}
              approvers={selectedItem?.approvers || []}
            />
            <ApproverRow
              level={RoleType.L2}
              approvers={selectedItem?.approvers || []}
            />
            <View style={styles.infoRow}>
              <Text
                style={[
                  styles.labelText,
                  mediaStyles({
                    desktopStyle: {
                      ...TYPOGRAPHY.CAPTION1,
                    },
                  }),
                ]}
              >
                Rejection Reason:{' '}
              </Text>
              <Text
                style={[
                  styles.valueText,
                  selectedItem?.remarks || selectedItem?.reason_config
                    ? TEXT_STYLES.textError100
                    : TEXT_STYLES.textNeutral90,
                  mediaStyles({
                    desktopStyle: {
                      ...TYPOGRAPHY.CAPTION1,
                    },
                  }),
                ]}
              >
                {selectedItem?.remarks || selectedItem?.reason_config || '--'}
              </Text>
            </View>
          </View>
          {(roleType === RoleType.L1 &&
            selectedItem?.latest_status == STATUS.L1_PENDING) ||
          (roleType === RoleType.L2 &&
            selectedItem?.latest_status == STATUS.L2_PENDING) ? (
            <View
              style={[
                COMMON_STYLES.row,
                COMMON_STYLES.justifyBetween,
                { gap: 12, marginTop: 16 },
              ]}
            >
              <Button
                buttonStyle={[
                  BG_STYLES.bgError10,
                  { flex: 1, paddingVertical: 20, borderColor: 'transparent' },
                ]}
                onClick={() => {
                  setShowDrawer(false)
                  setShowRejectDrawer(true)
                }}
                type={BUTTON_TYPE.PRIMARY}
                text="Reject"
                size={SIZE.small}
                status={BUTTON_STATUS.ACTIVE}
                textStyle={[TEXT_STYLES.textError100]}
                iconLeft={
                  <CrossIcon height={16} width={16} strokeColor={'error100'} />
                }
              />
              <Button
                buttonStyle={[
                  BG_STYLES.bgPrimary10,
                  COMMON_STYLES.flex1,
                  { paddingVertical: 20, borderColor: 'transparent' },
                ]}
                onClick={() => {
                  setShowDrawer(false)
                  setConfirmAction(RequestAction.APPROVE)
                  setShowConfirm(true)
                }}
                type={BUTTON_TYPE.PRIMARY}
                text="Approve"
                size={SIZE.small}
                status={BUTTON_STATUS.ACTIVE}
                textStyle={[TEXT_STYLES.textPrimary100]}
                iconLeft={
                  <ApproveIcon
                    width={16}
                    height={16}
                    color={COLORS.primary100}
                  />
                }
              />
            </View>
          ) : (
            canWithDrawRequest && (
              <Button
                buttonStyle={[
                  BG_STYLES.bgError10,
                  {
                    flex: 1,
                    paddingVertical: 12,
                    borderColor: 'transparent',
                    marginTop: 12,
                  },
                ]}
                onClick={() => handleWithDraw(selectedItem!.id)}
                type={BUTTON_TYPE.PRIMARY}
                text="Withdraw Request"
                size={SIZE.small}
                status={BUTTON_STATUS.ACTIVE}
                textStyle={[TEXT_STYLES.textError100]}
                iconLeft={
                  <DeleteIcon
                    svgProps={styles.image16x16}
                    pathProps={{ stroke: COLORS.error100 }}
                  />
                }
              />
            )
          )}
        </BottomDrawer>
      )}
      {isDesktop ? (
        <CenterModal
          isVisible={showConfirm}
          allowClose={false}
          onClose={() => setShowConfirm(false)}
        >
          <View
            style={[
              COMMON_STYLES.row,
              COMMON_STYLES.fullWidth,
              COMMON_STYLES.justifyBetween,
              COMMON_STYLES['pv1/2'],
            ]}
          >
            <Text
              style={[
                VARIANTS_STYLES.SEMI_EMPHASIZED,
                TEXT_STYLES.textNeutral80,
                TYPOGRAPHY.HEADING3,
              ]}
            >
              {confirmTitle}
            </Text>
            <Pressable onPress={() => setShowConfirm(false)}>
              <CrossIcon fill="none" height={25} width={25} />
            </Pressable>
          </View>
          <Text
            style={[
              VARIANTS_STYLES.BASE,
              TEXT_STYLES.textNeutral90,
              TYPOGRAPHY.TEXT2,
              COMMON_STYLES.pb2,
            ]}
          >
            {confirmMessage}
          </Text>
          <View
            style={[COMMON_STYLES.row, COMMON_STYLES.fullWidth, { gap: 12 }]}
          >
            <Button
              buttonStyle={[
                BG_STYLES.bgNeutral00,
                COMMON_STYLES.flex1,
                { paddingVertical: 24 },
              ]}
              onClick={() => setShowConfirm(false)}
              type={BUTTON_TYPE.PRIMARY}
              text="Cancel"
              size={SIZE.small}
              status={BUTTON_STATUS.ACTIVE}
              textStyle={[TEXT_STYLES.textNeutral70, TYPOGRAPHY.TEXT1]}
            />
            <Button
              buttonStyle={[
                BG_STYLES.bgPrimary100,
                COMMON_STYLES.flex1,
                { paddingVertical: 24, borderColor: 'transparent' },
              ]}
              onClick={() => {
                setShowConfirm(false)
                {
                  confirmAction === RequestAction.APPROVE
                    ? handleAction(RequestAction.APPROVE, [selectedItem!.id])
                    : handleAction(RequestAction.REJECT, [selectedItem!.id])
                }
              }}
              type={BUTTON_TYPE.PRIMARY}
              text={
                confirmAction === RequestAction.APPROVE ? 'Approve' : 'Reject'
              }
              size={SIZE.small}
              status={BUTTON_STATUS.ACTIVE}
              textStyle={[TEXT_STYLES.textNeutral00, TYPOGRAPHY.TEXT1]}
            />
          </View>
        </CenterModal>
      ) : (
        showConfirm && (
          <BottomDrawer
            visible={showConfirm}
            onClose={() => setShowConfirm(false)}
            closeOnBackdropPress
            heading={confirmTitle}
          >
            <Text
              style={[
                VARIANTS_STYLES.BASE,
                TEXT_STYLES.textNeutral90,
                TYPOGRAPHY.TEXT2,
                COMMON_STYLES.pb2,
              ]}
            >
              {confirmMessage}
            </Text>

            <View style={[COMMON_STYLES.row, { gap: 8 }]}>
              <Button
                buttonStyle={[
                  BG_STYLES.bgNeutral00,
                  COMMON_STYLES.flex1,
                  { paddingVertical: 24 },
                ]}
                onClick={() => {
                  setShowConfirm(false)
                }}
                type={BUTTON_TYPE.PRIMARY}
                text="Cancel"
                size={SIZE.small}
                status={BUTTON_STATUS.ACTIVE}
                textStyle={[TEXT_STYLES.textNeutral70, TYPOGRAPHY.TEXT1]}
              />
              <Button
                buttonStyle={[
                  BG_STYLES.bgPrimary100,
                  COMMON_STYLES.flex1,
                  { paddingVertical: 24, borderColor: 'transparent' },
                ]}
                onClick={() => {
                  {
                    confirmAction === RequestAction.APPROVE
                      ? handleAction(RequestAction.APPROVE, [selectedItem!.id])
                      : handleAction(RequestAction.REJECT, [selectedItem!.id])
                  }
                  setShowConfirm(false)
                }}
                type={BUTTON_TYPE.PRIMARY}
                text={
                  confirmAction === RequestAction.APPROVE ? 'Approve' : 'Reject'
                }
                size={SIZE.small}
                status={BUTTON_STATUS.ACTIVE}
                textStyle={[TEXT_STYLES.textNeutral00, TYPOGRAPHY.TEXT1]}
              />
            </View>
          </BottomDrawer>
        )
      )}

      {showFilterModal && (
        <FilterModal
          filterPage={'change_request'}
          appliedFilterData={appliedFilters}
          onApplyFilters={handleApplyFilters}
          onClose={() => setShowFilterModal(false)}
        />
      )}
      {isEnabled && statusFilter == STATUS_FILTERS[0] && (
        <View
          style={[
            styles.btnWrapper,
            {
              elevation: 2,
              bottom: 0,
              borderTopColor: COLORS.neutral20,
              borderTopWidth: 1,
              gap: 8,
            },
            mediaStyles({
              desktopStyle: {
                bottom: 12,
              },
            }),
          ]}
        >
          <Button
            buttonStyle={[
              selectedItems.length > 0 && BG_STYLES.bgError100,
              styles.btnStyle,
            ]}
            onClick={() => {
              setShowDrawer(false)
              setShowRejectDrawer(true)
            }}
            type={BUTTON_TYPE.PRIMARY}
            text="Reject"
            size={SIZE.small}
            status={
              selectedItems.length > 0
                ? BUTTON_STATUS.ACTIVE
                : BUTTON_STATUS.DISABLED
            }
            textStyle={[selectedItems.length > 0 && TEXT_STYLES.textNeutral00]}
            iconLeft={
              <CrossIcon
                height={16}
                width={16}
                strokeColor={
                  (selectedItems.length > 0 && 'neutral00') || 'neutral40'
                }
              />
            }
          />
          <Button
            buttonStyle={[
              selectedItems.length > 0 && BG_STYLES.bgPrimary100,
              styles.btnStyle,
            ]}
            onClick={() => handleAction(RequestAction.APPROVE)}
            type={BUTTON_TYPE.PRIMARY}
            text="Approve"
            size={SIZE.small}
            status={
              selectedItems.length > 0
                ? BUTTON_STATUS.ACTIVE
                : BUTTON_STATUS.DISABLED
            }
            textStyle={[selectedItems.length > 0 && TEXT_STYLES.textNeutral00]}
            iconLeft={
              <ApproveIcon
                width={16}
                height={16}
                color={
                  selectedItems.length > 0 ? COLORS.neutral00 : COLORS.neutral40
                }
              />
            }
          />
        </View>
      )}
      {showRoleSelection && availableRoles.length > 1 && (
        <RoleSelection
          visible={showRoleSelection}
          roles={availableRoles}
          onSelect={handleRoleSelect}
        />
      )}

      <CenterModal isVisible={showNoRole} allowClose={false} onClose={() => {}}>
        <View
          style={[
            COMMON_STYLES.row,
            COMMON_STYLES.fullWidth,
            COMMON_STYLES.justifyBetween,
            COMMON_STYLES['pv1/2'],
          ]}
        >
          <Text
            style={[
              VARIANTS_STYLES.SEMI_EMPHASIZED,
              TEXT_STYLES.textNeutral80,
              TYPOGRAPHY.HEADING3,
            ]}
          >
            No Role Found
          </Text>
        </View>
        <Text
          style={[
            VARIANTS_STYLES.BASE,
            TEXT_STYLES.textNeutral90,
            TYPOGRAPHY.TEXT2,
            COMMON_STYLES.pb2,
          ]}
        >
          Your account does not have the required role to access this page.
        </Text>
        <View style={[COMMON_STYLES.row, COMMON_STYLES.fullWidth, { gap: 12 }]}>
          <Button
            buttonStyle={[
              BG_STYLES.bgPrimary100,
              COMMON_STYLES.flex1,
              { paddingVertical: 24, borderColor: 'transparent' },
            ]}
            onClick={() => {
              setNoRole(false)
              if ('ReactNativeWebView' in window) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                postMessageToWebView(window, {
                  code: 'closeScreen',
                })
                return
              }
            }}
            type={BUTTON_TYPE.PRIMARY}
            text="Okay"
            size={SIZE.small}
            status={BUTTON_STATUS.ACTIVE}
            textStyle={[TEXT_STYLES.textNeutral00, TYPOGRAPHY.TEXT1]}
          />
        </View>
      </CenterModal>
      {showRejectDrawer && (
        <BottomDrawer
          visible={showRejectDrawer}
          onClose={() => {
            setShowRejectDrawer(false)
            setSelectedReasonIndex(-1)
            setOtherReason('')
          }}
          closeOnBackdropPress
          heading="Reject Request?"
        >
          <View>
            <Text
              style={[
                TEXT_STYLES.textNeutral90,
                TYPOGRAPHY.TEXT2,
                VARIANTS_STYLES.BASE,
              ]}
            >
              Please select a reason for rejection.
            </Text>
            {rejectLoading && <Spinner />}
            {!rejectLoading && (
              <>
                <RadioButton
                  list={reasonList.map(reason => ({
                    value: reason.id,
                    radioElement: (
                      <View
                        style={[
                          COMMON_STYLES.row,
                          COMMON_STYLES.alignItemsCenter,
                          COMMON_STYLES.flex1,
                        ]}
                      >
                        {reason.id === -1 ? (
                          <>
                            <OutlinedInput
                              label="Other, Specify"
                              placeholder="Reason here..."
                              multiline
                              value={otherReason}
                              onChangeText={setOtherReason}
                              containerStyle={[
                                COMMON_STYLES.flex1,
                                { marginLeft: 10 },
                              ]}
                            />
                          </>
                        ) : (
                          <Text
                            style={[
                              TEXT_STYLES.textNeutral70,
                              TYPOGRAPHY.TEXT2,
                              VARIANTS_STYLES.MEDIUM,
                              COMMON_STYLES.ml1,
                            ]}
                          >
                            {reason.reason}
                          </Text>
                        )}
                      </View>
                    ),
                  }))}
                  selected={selectedReasonIndex}
                  onClick={index => setSelectedReasonIndex(index)}
                  radioTextContainerStyleObj={[
                    COMMON_STYLES.row,
                    COMMON_STYLES.alignItemsCenter,
                    COMMON_STYLES.flex1,
                  ]}
                />

                <View
                  style={[
                    COMMON_STYLES.row,
                    COMMON_STYLES['mt5/4'],
                    { gap: 12 },
                  ]}
                >
                  <Button
                    buttonStyle={[
                      BG_STYLES.bgNeutral00,
                      COMMON_STYLES.flex1,
                      COMMON_STYLES['pv3/2'],
                    ]}
                    type={BUTTON_TYPE.PRIMARY}
                    text="Cancel"
                    onClick={() => {
                      setShowRejectDrawer(false)
                      setSelectedReasonIndex(-1)
                      setOtherReason('')
                    }}
                    size={SIZE.small}
                    status={BUTTON_STATUS.ACTIVE}
                    textStyle={[TEXT_STYLES.textNeutral70, TYPOGRAPHY.TEXT1]}
                  />

                  <Button
                    buttonStyle={[
                      BG_STYLES.bgError100,
                      COMMON_STYLES.flex1,
                      COMMON_STYLES['pv3/2'],
                      { borderColor: 'transparent' },
                    ]}
                    status={
                      selectedReasonIndex === -1 ||
                      (reasonList[selectedReasonIndex]?.id === -1 &&
                        !otherReason.trim())
                        ? BUTTON_STATUS.DISABLED
                        : BUTTON_STATUS.ACTIVE
                    }
                    type={BUTTON_TYPE.PRIMARY}
                    text="Reject"
                    textStyle={[TEXT_STYLES.textNeutral00, TYPOGRAPHY.TEXT1]}
                    onClick={() => {
                      const selected = reasonList[selectedReasonIndex]
                      const reasonPayload =
                        selected.id === -1
                          ? {
                              reason_config_id: null,
                              reason: otherReason.trim(),
                            }
                          : {
                              reason_config_id: selected.id,
                              reason: '',
                            }
                      handleAction(
                        RequestAction.REJECT,
                        [selectedItem!.id],
                        reasonPayload,
                      )
                      setShowRejectDrawer(false)
                      setSelectedReasonIndex(-1)
                      setOtherReason('')
                    }}
                  />
                </View>
              </>
            )}
          </View>
        </BottomDrawer>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    justifyContent: 'center',
    maxWidth: 950,
    width: '100%',
    alignSelf: 'center',
  },
  container: {
    flex: 1,
  },
  contentArea: {
    flex: 1,
  },
  labelText: {
    ...TEXT_STYLES.textNeutral70,
    ...TYPOGRAPHY.CAPTION2,
    ...VARIANTS_STYLES.BASE,
    flex: 0.3,
  },
  valueText: {
    ...TEXT_STYLES.textNeutral90,
    ...TYPOGRAPHY.CAPTION2,
    ...VARIANTS_STYLES.BASE,
    flex: 0.4,
  },
  infoRow: {
    ...COMMON_STYLES.row,
    marginBottom: 4,
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    ...TYPOGRAPHY.TEXT2,
    ...VARIANTS_STYLES.BASE,
    ...TEXT_STYLES.textNeutral80,
  },
  filterBtn: {
    padding: 4,
    ...BORDER_STYLES.borderNeutral20,
    ...COMMON_STYLES.b1,
    ...BG_STYLES.bgWhite,
    borderRadius: 5,
    marginRight: 12,
    width: 32,
    height: 32,
  },
  statusText: {
    ...TYPOGRAPHY.CAPTION2,
    ...VARIANTS_STYLES.SEMI_EMPHASIZED,
    flex: 0.3,
  },
  image16x16: { height: 16, width: 16 },
  selectAll: {
    ...TYPOGRAPHY.CAPTION1,
    ...VARIANTS_STYLES.MEDIUM,
  },
  btnWrapper: {
    ...COMMON_STYLES.row,
    ...BG_STYLES.bgWhite,
    ...COMMON_STYLES.fullWidth,
    ...COMMON_STYLES.p1,
    zIndex: 10,
  },
  btnStyle: {
    flex: 1,
    paddingVertical: 20,
    borderColor: 'transparent',
  },
  checkbox: {
    borderRadius: 3,
  },
})

export default HomePriceRequest
